import {
  CustomAmountFieldDto,
  CustomBooleanFieldDto,
  CustomContractDataFieldDto,
  CustomDateFieldDto,
  CustomEmailFieldDto,
  CustomEnumFieldDto,
  CustomMultiEnumFieldDto,
  CustomNumberFieldDto,
  CustomPercentageFieldDto,
  CustomPersonFieldDto,
  CustomPhoneFieldDto,
  CustomRichTextFieldDto,
  CustomTagFieldDto,
  CustomTextFieldDto,
  CustomUrlFieldDto,
  NumberFieldDto,
  Populater,
  ProductCommentDto,
} from "../autogen/bff-api";

export interface ProductGroup {
  id: string;
  sourcingEventId?: string | null;
  projectId?: string | null;
  name: string;
  products?: Product[];
  productFields: ProductTemplateField[];
  originProductGroupId?: string | null;
}

export type ProductGroupDraft = Omit<ProductGroup, "id">;

export interface Product {
  id: string;
  quantity: NumberFieldDto;
  unitPrice?: NumberFieldDto;
  productFields: ProductField[];
  conversation?: ProductCommentDto[];
}

export type ProductDraft = Omit<Product, "id"> & { id?: string };

export type ProductFieldType = "Text" | "Number" | "Boolean" | "Quantity";

export const productFieldTypes: ProductFieldType[] = ["Text", "Boolean", "Number"];

export interface ProductTemplateField {
  id: string;
  name: string;
  type: ProductFieldType;
  populatedBy: Populater;
  isRequired: boolean;
}

export type ProductField = ProductTemplateField & { value?: number | string | boolean | null };

export const isProductDraft = (product: Product | ProductDraft) => {
  return !(product as Product).id;
};

export enum FetchState {
  Idle = "Idle",
  Loading = "Loading",
  Error = "Error",
  Success = "Success",
}

export type CounterpartyContext =
  | {
      organizationEntryId: string;
      organizationId?: never;
    }
  | {
      organizationId: string;
      organizationEntryId?: never;
    };

// Type guards for custom contract fields
export const isCustomAmountField = (field: CustomContractDataFieldDto): field is CustomAmountFieldDto => {
  return field.type === "CustomAmount";
};

export const isCustomNumberField = (field: CustomContractDataFieldDto): field is CustomNumberFieldDto => {
  return field.type === "CustomNumber";
};

export const isCustomBooleanField = (field: CustomContractDataFieldDto): field is CustomBooleanFieldDto => {
  return field.type === "CustomBoolean";
};

export const isCustomDateField = (field: CustomContractDataFieldDto): field is CustomDateFieldDto => {
  return field.type === "CustomDate";
};

export const isCustomEmailField = (field: CustomContractDataFieldDto): field is CustomEmailFieldDto => {
  return field.type === "CustomEmail";
};

export const isCustomEnumField = (field: CustomContractDataFieldDto): field is CustomEnumFieldDto => {
  return field.type === "CustomEnum";
};

export const isCustomMultiEnumField = (field: CustomContractDataFieldDto): field is CustomMultiEnumFieldDto => {
  return field.type === "CustomMultiEnum";
};

export const isCustomPercentageField = (field: CustomContractDataFieldDto): field is CustomPercentageFieldDto => {
  return field.type === "CustomPercentage";
};

export const isCustomPersonField = (field: CustomContractDataFieldDto): field is CustomPersonFieldDto => {
  return field.type === "CustomPerson";
};

export const isCustomPhoneField = (field: CustomContractDataFieldDto): field is CustomPhoneFieldDto => {
  return field.type === "CustomPhone";
};

export const isCustomRichTextField = (field: CustomContractDataFieldDto): field is CustomRichTextFieldDto => {
  return field.type === "CustomRichText";
};

export const isCustomTagField = (field: CustomContractDataFieldDto): field is CustomTagFieldDto => {
  return field.type === "CustomTag";
};

export const isCustomTextField = (field: CustomContractDataFieldDto): field is CustomTextFieldDto => {
  return field.type === "CustomText";
};

export const isCustomUrlField = (field: CustomContractDataFieldDto): field is CustomUrlFieldDto => {
  return field.type === "CustomUrl";
};

// Shared JSON Schema types for AI response schemas
export type JsonSchemaProperty =
  | ObjectSchemaProperty
  | ArraySchemaProperty
  | StringSchemaProperty
  | NumberSchemaProperty
  | BooleanSchemaProperty;

export interface ObjectSchemaProperty {
  type: "object";
  properties: Record<string, JsonSchemaProperty>;
  required?: string[];
  additionalProperties: false;
}

export interface ArraySchemaProperty {
  type: "array";
  items: JsonSchemaProperty;
}

export interface StringSchemaProperty {
  type: "string";
  enum?: string[];
}

export interface NumberSchemaProperty {
  type: "number";
  minimum?: number;
  maximum?: number;
}

export interface BooleanSchemaProperty {
  type: "boolean";
}

export interface JsonSchema extends ObjectSchemaProperty {
  type: "object";
  properties: Record<string, JsonSchemaProperty>;
  required: string[];
  additionalProperties: false;
}

export interface JsonSchemaDto {
  name: string;
  schema: string;
  strict: boolean;
}

export interface RequirementFulfillment {
  requirement: string;
  fulfilled: "yes" | "no" | "partly";
}

export interface BidEvaluation {
  bidId: string;
  score: number;
  strengths: string[];
  weaknesses: string[];
  recommendation: Recommendation;
  comments: string;
  totalPrice: number;
  requirements: RequirementFulfillment[];
}

export type Recommendation = "recommended" | "needs clarifications" | "not recommended";

export interface EvaluationSummary {
  bestBid: string;
  executiveSummary: string;
  overallRecommendation: string;
  keyInsights: string[];
}

export interface ComparisonResult {
  summary: EvaluationSummary;
  perBidSummary: {
    bidId: string;
    comparisonSummary: string;
    keyInsights: string[];
    recommendation: {
      isRecommended: boolean;
      rationale: string;
    };
  }[];
}

export interface SingleEvaluationResult {
  evaluation: BidEvaluation;
}

export interface RequirementsResult {
  requirements: string[];
}

export const createSingleBidEvaluationSchema = (): JsonSchemaDto => {
  const schema: JsonSchema = {
    type: "object",
    properties: {
      evaluation: {
        type: "object",
        properties: {
          bidId: { type: "string" },
          score: { type: "number", minimum: 0, maximum: 100 },
          strengths: {
            type: "array",
            items: { type: "string" },
          },
          weaknesses: {
            type: "array",
            items: { type: "string" },
          },
          recommendation: { type: "string", enum: ["recommended", "needs clarifications", "not recommended"] },
          comments: { type: "string" },
          totalPrice: { type: "number", minimum: 0 },
          requirements: {
            type: "array",
            items: {
              type: "object",
              properties: {
                requirement: { type: "string" },
                fulfilled: { type: "string", enum: ["yes", "no", "partly"] },
              },
              required: ["requirement", "fulfilled"],
              additionalProperties: false,
            },
          },
        },
        required: [
          "bidId",
          "score",
          "strengths",
          "weaknesses",
          "recommendation",
          "comments",
          "totalPrice",
          "requirements",
        ],
        additionalProperties: false,
      },
    },
    required: ["evaluation"],
    additionalProperties: false,
  };

  return {
    name: "SingleBidEvaluationSchema_v4",
    schema: JSON.stringify(schema),
    strict: true,
  };
};

export const comparisonSchema: JsonSchemaDto = {
  name: "BidsComparisonSchema_v7",
  strict: true,
  schema: JSON.stringify({
    type: "object",
    properties: {
      summary: {
        type: "object",
        properties: {
          bestBid: { type: ["string", "null"] },
          overallRecommendation: { type: "string" },
          executiveSummary: {
            type: "string",
            description: `
            A short summary of the sourcing event, the bids delivered and what makes the best bid the best bid.
              
            Bid ids should not be included.
              `,
          },
          keyInsights: {
            type: "array",
            items: { type: "string" },
          },
        },
        required: ["bestBid", "overallRecommendation", "executiveSummary", "keyInsights"],
        additionalProperties: false,
      },
      perBidSummary: {
        type: "array",
        items: {
          type: "object",
          properties: {
            bidId: {
              type: "string",
              description: "The UUID of the bid this summary refers to",
            },
            comparisonSummary: {
              type: "string",
              description: "How this bid compares to the other bids",
            },
            keyInsights: {
              type: "array",
              description: "Key insights specific to this bid",
              items: { type: "string" },
            },
            recommendation: {
              type: "object",
              description: "Whether this bid/supplier is recommended and why",
              properties: {
                isRecommended: {
                  type: "boolean",
                  description: "True if this bid/supplier is recommended overall",
                },
                rationale: {
                  type: "string",
                  description: "Explanation of why it is or isn't recommended",
                },
              },
              required: ["isRecommended", "rationale"],
              additionalProperties: false,
            },
          },
          required: ["bidId", "comparisonSummary", "keyInsights", "recommendation"],
          additionalProperties: false,
        },
      },
    },
    required: ["summary", "perBidSummary"],
    additionalProperties: false,
  }),
};

export const requirementsSchema: JsonSchemaDto = {
  name: "RequirementsSchema_v1",
  schema: JSON.stringify({
    type: "object",
    properties: {
      requirements: { type: "array", items: { type: "string" }, minItems: 1, maxItems: 10 },
    },
    required: ["requirements"],
    additionalProperties: false,
  }),
  strict: true,
};
